import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Container,
  Row,
  Col,
  Form,
  Button,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
  Modal,
  Offcanvas,
  Accordion,
  Badge,
  Spinner,
} from "react-bootstrap";
import { getUserPracticeById } from "../../api";
import { getAllocationReport } from "../../afinanceapi";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import "./CreateInvoice.css";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function AllocationReport() {
  const [receipts, setReceipts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totals, setTotals] = useState({ sumTotal: 0, sumExclVAT: 0, sumVAT: 0 });
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [practiceList, setPracticeList] = useState([]);

  // Available filter options (from backend, refreshed on date/practice change)
  const [availableMethods, setAvailableMethods] = useState([]);
  const [availablePractitioners, setAvailablePractitioners] = useState([]);

  // Committed filters (drive the table)
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [selectedPractitioners, setSelectedPractitioners] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Temporary filters while panel is open
  const [tmpPractices, setTmpPractices] = useState([]);
  const [tmpMethods, setTmpMethods] = useState([]);
  const [tmpPractitioners, setTmpPractitioners] = useState([]);
  const [tmpStartDate, setTmpStartDate] = useState("");
  const [tmpEndDate, setTmpEndDate] = useState("");

  const [page, setPage] = useState(1);
  const limit = 20;

  const [exportLoading, setExportLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();

  const currency = new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" });

  // Init filters from URL params on mount; fall back to last 1 month default (per D-04)
  useEffect(() => {
    const p = query.get('practices');
    const m = query.get('methods');
    const pr = query.get('practitioners');
    const st = query.get('start');
    const en = query.get('end');

    if (st || en || p || m || pr) {
      // Restore from URL params
      if (p) { const arr = p.split(','); setSelectedPractices(arr); setTmpPractices(arr); }
      if (m) { const arr = m.split(','); setSelectedMethods(arr); setTmpMethods(arr); }
      if (pr) { const arr = pr.split(','); setSelectedPractitioners(arr); setTmpPractitioners(arr); }
      if (st) { setStartDate(st); setTmpStartDate(st); }
      if (en) { setEndDate(en); setTmpEndDate(en); }
    } else {
      // Default: last 1 month
      const today = new Date();
      const start = new Date();
      start.setMonth(start.getMonth() - 1);
      const iso = (d) => d.toISOString().slice(0, 10);
      setStartDate(iso(start));
      setEndDate(iso(today));
      setTmpStartDate(iso(start));
      setTmpEndDate(iso(today));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch practices
  useEffect(() => {
    async function fetchPractices() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("No user token found");
        const user = jwtDecode(token);
        const practices = await getUserPracticeById(user._id);
        setPracticeList(practices || []);
      } catch (e) {
        console.error("Error fetching practices:", e);
        setPracticeList([]);
      }
    }
    fetchPractices();
  }, []);

  // Backend-driven fetch — fires on every filter/sort/page change
  useEffect(() => {
    if (!startDate || !endDate || practiceList.length === 0) return;

    async function fetchData() {
      try {
        setLoading(true);
        const practiceNums =
          selectedPractices.length > 0
            ? practiceList
                .filter((p) => selectedPractices.includes(p.practiceName))
                .map((p) => p.practiceNumber)
            : practiceList.map((p) => p.practiceNumber);

        const result = await getAllocationReport({
          practiceNumbers: practiceNums,
          startDate,
          endDate,
          methods: selectedMethods,
          practitioners: selectedPractitioners,
          sortKey: sortConfig.key,
          sortDir: sortConfig.direction,
          page,
          limit,
        });

        // Map to component row format
        const rows = (result.data || []).map((item) => ({
          receiptDate: item.receiptDate || "",
          receiptNumber: item.receiptNumber || "",
          method: item.receiptMethod || "",
          receiptReference: item.receiptReference || "",
          invoiceNumber: item.invoiceNumber || "",
          invoiceDate: item.invoiceDateFromReceipt || "",
          practitionerFirstName: item.practitionerFirstName || "",
          practitionerLastName: item.practitionerLastName || "",
          receiptAmount: Number(item.paymentAmount) || 0,
          amountExclVAT: Number(item.amountExclVAT) || 0,
          vatAmount: Number(item.vatAmount) || 0,
          practiceName: item.practiceName || "",
          invoiceId: item.invoiceId,
          accountId: item.accountId,
          accountName: item.accountName || "",
          paymentSource: item.paymentSource || "",
          _rowId: `${item.receiptId}-${item.invoiceId}-${item.receiptNumber}`,
        }));

        setReceipts(rows);
        setTotalCount(result.total || 0);
        setTotals(result.totals || { sumTotal: 0, sumExclVAT: 0, sumVAT: 0 });

        // Update available filter options (only when methods/practitioners are not yet filtered)
        if (result.availableMethods) setAvailableMethods(result.availableMethods);
        if (result.availablePractitioners) setAvailablePractitioners(result.availablePractitioners);
      } catch (e) {
        console.error("Error fetching receipts:", e);
        setReceipts([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [practiceList, selectedPractices, selectedMethods, selectedPractitioners, startDate, endDate, sortConfig, page]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedPractices, selectedMethods, selectedPractitioners, startDate, endDate]);

  const allPractices = useMemo(
    () => (practiceList || []).map((p) => p.practiceName).filter(Boolean),
    [practiceList]
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  const toggleIn = (arr, value) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
    setPage(1);
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="fa-solid fa-arrows-up-down text-primary ms-2" />;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  };

  // Determine if any of the selected practices are VAT registered
  const hasVATRegisteredPractice = useMemo(() => {
    const relevant = selectedPractices.length
      ? practiceList.filter((p) => selectedPractices.includes(p.practiceName))
      : practiceList;
    return relevant.some((p) => p.vatNumber && p.vatNumber.trim() !== "");
  }, [practiceList, selectedPractices]);

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const practiceNums =
        selectedPractices.length > 0
          ? practiceList
              .filter((p) => selectedPractices.includes(p.practiceName))
              .map((p) => p.practiceNumber)
          : practiceList.map((p) => p.practiceNumber);

      const result = await getAllocationReport({
        practiceNumbers: practiceNums,
        startDate,
        endDate,
        methods: selectedMethods,
        practitioners: selectedPractitioners,
        sortKey: sortConfig.key,
        sortDir: sortConfig.direction,
        page: 1,
        limit: 99999,
      });

      const toDateStr = (v) => (v ? String(v).slice(0, 10) : "");
      const allRows = (result.data || []).map((item) => ({
        ReceiptDate: toDateStr(item.receiptDate),
        ReceiptNumber: item.receiptNumber || "",
        ReceiptReference: item.receiptReference || "",
        MethodOfPayment: item.receiptMethod || "",
        PaymentSource: item.paymentSource || "",
        InvoiceNumber: item.invoiceNumber || "",
        InvoiceDate: toDateStr(item.invoiceDateFromReceipt),
        AccountName: item.accountName || "",
        PractitionerFirstName: item.practitionerFirstName || "",
        PractitionerLastName: item.practitionerLastName || "",
        ReceivedInclVAT: Number(item.paymentAmount) || 0,
        ReceivedExclVAT: Number(item.amountExclVAT) || 0,
        VATAmount: Number(item.vatAmount) || 0,
        PracticeName: item.practiceName || "",
      }));

      const ExcelJS = (await import("exceljs")).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Allocation Report");

      const moneyFmt = '#,##0.00;[Red]-#,##0.00;""';

      ws.columns = [
        { header: "Receipt Date",         key: "ReceiptDate",           width: 13 },
        { header: "Receipt #",            key: "ReceiptNumber",         width: 14 },
        { header: "Reference",            key: "ReceiptReference",      width: 14 },
        { header: "Method",               key: "MethodOfPayment",       width: 14 },
        { header: "Source",               key: "PaymentSource",         width: 14 },
        { header: "Invoice #",            key: "InvoiceNumber",         width: 14 },
        { header: "Invoice Date",         key: "InvoiceDate",           width: 13 },
        { header: "Account Name",         key: "AccountName",           width: 30 },
        { header: "Practitioner First",   key: "PractitionerFirstName", width: 18 },
        { header: "Practitioner Last",    key: "PractitionerLastName",  width: 18 },
        { header: "Received (Incl VAT)",  key: "ReceivedInclVAT",       width: 17, style: { numFmt: moneyFmt } },
        { header: "Received (Excl VAT)",  key: "ReceivedExclVAT",       width: 17, style: { numFmt: moneyFmt } },
        { header: "VAT",                  key: "VATAmount",             width: 12, style: { numFmt: moneyFmt } },
        { header: "Practice Name",        key: "PracticeName",          width: 26 },
      ];

      ws.addRows(allRows);

      ws.getRow(1).font = { bold: true };
      ws.views = [{ state: "frozen", ySplit: 1 }];
      ws.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: ws.columns.length },
      };

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, "allocation-report.xlsx");
    } catch (e) {
      console.error("Export failed:", e);
    } finally {
      setExportLoading(false);
    }
  };

  const openFilters = () => {
    setTmpPractices(selectedPractices);
    setTmpMethods(selectedMethods);
    setTmpPractitioners(selectedPractitioners);
    setTmpStartDate(startDate);
    setTmpEndDate(endDate);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setSelectedPractices(tmpPractices);
    setSelectedMethods(tmpMethods);
    setSelectedPractitioners(tmpPractitioners);
    setStartDate(tmpStartDate);
    setEndDate(tmpEndDate);
    setPage(1);
    setShowFilters(false);

    // Write filters to URL — preserve existing params (e.g. section=) then set/clear filter keys
    const params = new URLSearchParams(location.search);
    params.delete('practices'); params.delete('methods'); params.delete('practitioners'); params.delete('start'); params.delete('end');
    if (tmpPractices.length) params.set('practices', tmpPractices.join(','));
    if (tmpMethods.length) params.set('methods', tmpMethods.join(','));
    if (tmpPractitioners.length) params.set('practitioners', tmpPractitioners.join(','));
    if (tmpStartDate) params.set('start', tmpStartDate);
    if (tmpEndDate) params.set('end', tmpEndDate);
    navigate({ search: params.toString() }, { replace: true });
  };

  const clearFilters = () => {
    setSelectedPractices([]);
    setSelectedMethods([]);
    setSelectedPractitioners([]);
    const today = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    const iso = (d) => d.toISOString().slice(0, 10);
    const s = iso(start);
    const e = iso(today);
    setStartDate(s);
    setEndDate(e);
    setTmpPractices([]);
    setTmpMethods([]);
    setTmpPractitioners([]);
    setTmpStartDate(s);
    setTmpEndDate(e);
    // Preserve section= and other non-filter params
    const params = new URLSearchParams(location.search);
    params.delete('practices'); params.delete('methods'); params.delete('practitioners'); params.delete('start'); params.delete('end');
    navigate({ search: params.toString() }, { replace: true });
  };

  const activeCount =
    (selectedPractices.length ? 1 : 0) +
    (selectedMethods.length ? 1 : 0) +
    (selectedPractitioners.length ? 1 : 0) +
    (startDate && endDate ? 1 : 0);

  return (
    <Container fluid className="pt-2">
      <Row className="mb-3 align-items-center">
        <Col md={4}>
          <span className="text-primary fw-semibold">Total Receipts:</span>
          <Table bordered size="sm" className="mb-0 text-center align-middle">
            <thead className="table-primary">
              <tr>
                {hasVATRegisteredPractice && (
                  <>
                    <th className="fw-semibold text-primary" style={{ width: "33%" }}>Excluding VAT</th>
                    <th className="fw-semibold text-primary" style={{ width: "33%" }}>VAT</th>
                  </>
                )}
                <th className="fw-semibold text-primary" style={{ width: "33%" }}>Including VAT</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {hasVATRegisteredPractice && (
                  <>
                    <td><strong>{currency.format(totals.sumExclVAT)}</strong></td>
                    <td><strong>{currency.format(totals.sumVAT)}</strong></td>
                  </>
                )}
                <td><strong>{currency.format(totals.sumTotal)}</strong></td>
              </tr>
            </tbody>
          </Table>
        </Col>

        <Col md={8} className="d-flex gap-2 flex-wrap justify-content-end">
          <Button variant="primary" onClick={openFilters} style={{ width: "120px" }}>
            <i className="fa-solid fa-filter me-2" />
            Filter{" "}
            {activeCount > 0 && (
              <Badge className="ms-2" bg="light" text="dark">{activeCount}</Badge>
            )}
          </Button>
          <Button
            variant="outline-danger"
            onClick={clearFilters}
            style={{ width: "120px" }}
            disabled={
              selectedPractices.length === 0 &&
              selectedMethods.length === 0 &&
              selectedPractitioners.length === 0 &&
              !startDate &&
              !endDate
            }
          >
            <i className="fa-solid fa-trash me-2" />
            Filters
          </Button>

          {/* Pagination */}
          <div className="d-flex align-items-center gap-2 ms-2">
            <Button
              variant="primary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="fa-solid fa-angle-left" />
            </Button>
            <div className="text-nowrap small">Page {page} of {totalPages}</div>
            <Button
              variant="primary"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="fa-solid fa-angle-right" />
            </Button>
          </div>

          <ButtonGroup className="ms-1 gap-1">
            <OverlayTrigger placement="bottom" overlay={<Tooltip>Export to Excel</Tooltip>}>
              <Button variant="outline-secondary" onClick={handleExportExcel} disabled={exportLoading}>
                {exportLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className="fa-solid fa-file-export" />
                )}
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="bottom" overlay={<Tooltip>Info</Tooltip>}>
              <Button variant="outline-secondary" onClick={() => setShowInfo(true)}>
                <i className="fa-solid fa-circle-info" />
              </Button>
            </OverlayTrigger>
          </ButtonGroup>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading Allocations...</p>
        </div>
      ) : receipts.length === 0 ? (
        <p>No receipts found for the selected filters.</p>
      ) : (
        <Table bordered hover responsive className="fixed-table allocation-report-table">
          <thead className="table-primary text-center align-middle ms-1 me-1">
            <tr>
              <th style={{ width: "3%" }} className="text-primary fw-semibold">#</th>
              <th style={{ cursor: "pointer", width: "7%" }} onClick={() => handleSort("receiptDate")} className="text-primary fw-semibold">
                Date {renderSortIcon("receiptDate")}
              </th>
              <th style={{ cursor: "pointer", width: "10%" }} onClick={() => handleSort("receiptNumber")} className="text-primary fw-semibold">
                Receipt # {renderSortIcon("receiptNumber")}
              </th>
              <th style={{ cursor: "pointer", width: "7%" }} onClick={() => handleSort("receiptReference")} className="text-primary fw-semibold">
                Ref {renderSortIcon("receiptReference")}
              </th>
              <th style={{ cursor: "pointer", width: "10%" }} onClick={() => handleSort("method")} className="text-primary fw-semibold">
                Method {renderSortIcon("method")}
              </th>
              <th style={{ cursor: "pointer", width: "7%" }} onClick={() => handleSort("invoiceNumber")} className="text-primary fw-semibold">
                Inv # {renderSortIcon("invoiceNumber")}
              </th>
              <th style={{ cursor: "pointer", width: "10%" }} onClick={() => handleSort("invoiceDate")} className="text-primary fw-semibold">
                Inv Date {renderSortIcon("invoiceDate")}
              </th>
              <th style={{ cursor: "pointer", width: "14%" }} onClick={() => handleSort("accountName")} className="text-primary fw-semibold">
                Account {renderSortIcon("accountName")}
              </th>
              <th style={{ cursor: "pointer", width: "12%" }} onClick={() => handleSort("practitionerFirstName")} className="text-primary fw-semibold">
                Practitioner {renderSortIcon("practitionerFirstName")}
              </th>
              {hasVATRegisteredPractice && (
                <>
                  <th style={{ cursor: "pointer", width: "8%" }} onClick={() => handleSort("amountExclVAT")} className="text-primary fw-semibold text-end">
                    Excl. VAT {renderSortIcon("amountExclVAT")}
                  </th>
                  <th style={{ cursor: "pointer", width: "8%" }} onClick={() => handleSort("vatAmount")} className="text-primary fw-semibold text-end">
                    VAT {renderSortIcon("vatAmount")}
                  </th>
                </>
              )}
              <th style={{ cursor: "pointer", width: "8%" }} onClick={() => handleSort("receiptAmount")} className="text-primary fw-semibold text-end">
                Received {renderSortIcon("receiptAmount")}
              </th>
            </tr>
          </thead>
          <tbody>
            {receipts.map((row, idx) => {
              const rowNum = (page - 1) * limit + idx + 1;
              return (
                <tr
                  key={row._rowId}
                  style={{ cursor: row.invoiceId ? "pointer" : "default" }}
                  onClick={() => row.invoiceId && navigate(
                    `/invoices/posted/${row.invoiceId}`,
                    { state: { breadcrumbTrail: [{ label: 'Allocation Report', path: `${location.pathname}${location.search}` }] } }
                  )}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#6c757d")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                >
                  <td>{rowNum}</td>
                  <td>{row.receiptDate ? new Date(row.receiptDate).toLocaleDateString() : ""}</td>
                  <td>{row.receiptNumber}</td>
                  <td>{row.receiptReference}</td>
                  <td>{row.method}</td>
                  <td>{row.invoiceNumber}</td>
                  <td>{row.invoiceDate ? new Date(row.invoiceDate).toLocaleDateString() : ""}</td>
                  <td>{row.accountName}</td>
                  <td>{row.practitionerFirstName} {row.practitionerLastName}</td>
                  {hasVATRegisteredPractice && (
                    <>
                      <td className="text-end">{currency.format(Number(row.amountExclVAT) || 0)}</td>
                      <td className="text-end">{currency.format(Number(row.vatAmount) || 0)}</td>
                    </>
                  )}
                  <td className="text-end fw-bold">{currency.format(Number(row.receiptAmount) || 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Info modal */}
      <Modal show={showInfo} onHide={() => setShowInfo(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Allocation Report (Receipts)</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-muted">
          One row per <strong>receipt payment</strong> allocated to an invoice.
          Filters apply to the <strong>receipt date</strong> and selected practices.
          Totals reflect all filtered rows (not just this page). Excel export downloads the entire filtered dataset.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowInfo(false)}>Got it</Button>
        </Modal.Footer>
      </Modal>

      {/* Filter Offcanvas */}
      <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="start">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="d-flex align-items-center">
            <i className="fa-solid fa-filter me-2 text-primary" />
            Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="mb-3">
            <h6 className="text-primary fw-normal ms-3 mb-2">Receipt date range</h6>
            <div className="d-flex gap-2 ms-1">
              <Form.Control
                type="date"
                value={tmpStartDate}
                onChange={(e) => setTmpStartDate(e.target.value)}
              />
              <Form.Control
                type="date"
                value={tmpEndDate}
                onChange={(e) => setTmpEndDate(e.target.value)}
              />
            </div>
          </div>

          <Accordion alwaysOpen>
            {/* Practices */}
            <Accordion.Item eventKey="0">
              <Accordion.Header>Practices</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpPractices(allPractices)}>Select all</Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpPractices([])}>Clear</Button>
                </div>
                <div className="d-flex flex-column" style={{ maxHeight: 240, overflowY: "auto" }}>
                  {allPractices.map((name) => (
                    <Form.Check
                      key={name}
                      type="checkbox"
                      id={`practice-${name}`}
                      label={name}
                      checked={tmpPractices.includes(name)}
                      onChange={() => setTmpPractices((arr) => toggleIn(arr, name))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Payment method */}
            <Accordion.Item eventKey="1">
              <Accordion.Header>Payment method</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpMethods(availableMethods)} disabled={availableMethods.length === 0}>Select all</Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpMethods([])}>Clear</Button>
                </div>
                {availableMethods.length === 0 ? (
                  <div className="text-muted small">No methods in the current result set.</div>
                ) : (
                  <div className="d-flex flex-column" style={{ maxHeight: 240, overflowY: "auto" }}>
                    {availableMethods.map((m) => (
                      <Form.Check
                        key={m}
                        type="checkbox"
                        id={`method-${m}`}
                        label={m}
                        checked={tmpMethods.includes(m)}
                        onChange={() => setTmpMethods((arr) => toggleIn(arr, m))}
                      />
                    ))}
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>

            {/* Practitioners */}
            <Accordion.Item eventKey="2">
              <Accordion.Header>Practitioners</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpPractitioners(availablePractitioners)} disabled={availablePractitioners.length === 0}>Select all</Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => setTmpPractitioners([])}>Clear</Button>
                </div>
                {availablePractitioners.length === 0 ? (
                  <div className="text-muted small">No practitioners in the current result set.</div>
                ) : (
                  <div className="d-flex flex-column" style={{ maxHeight: 240, overflowY: "auto" }}>
                    {availablePractitioners.map((name) => (
                      <Form.Check
                        key={name}
                        type="checkbox"
                        id={`practitioner-${name}`}
                        label={name}
                        checked={tmpPractitioners.includes(name)}
                        onChange={() => setTmpPractitioners((arr) => toggleIn(arr, name))}
                      />
                    ))}
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          <div className="mt-4 d-flex gap-2">
            <Button onClick={applyFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2 text-white" />
              Apply
            </Button>
            <Button
              style={{ width: "120px" }}
              variant="outline-danger"
              onClick={() => {
                setTmpPractices([]);
                setTmpMethods([]);
                setTmpPractitioners([]);
                setTmpStartDate("");
                setTmpEndDate("");
              }}
            >
              <i className="fa-solid fa-trash me-2" />
              Remove
            </Button>
          </div>
          <div className="mt-2 text-muted small">
            Tip: leaving a section empty means "All".
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}
